import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import pendingActionService from '../services/pendingActionService';
import './CoachInvite.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const GOAL_OPTIONS = [
  { value: 'weight_loss', label: 'Weight Loss' },
  { value: 'muscle_gain', label: 'Muscle Gain' },
  { value: 'endurance', label: 'Endurance' },
  { value: 'flexibility', label: 'Flexibility' },
  { value: 'general_fitness', label: 'General Fitness' },
  { value: 'sport_performance', label: 'Sport Performance' },
  { value: 'rehabilitation', label: 'Rehabilitation' },
  { value: 'other', label: 'Other' },
];

const NUTRITION_GOAL_OPTIONS = [
  { value: 'weight_loss', label: 'Lose Weight' },
  { value: 'muscle_gain', label: 'Build Muscle' },
  { value: 'maintain', label: 'Maintain Weight' },
  { value: 'improve_energy', label: 'Improve Energy' },
  { value: 'manage_condition', label: 'Manage Health Condition' },
  { value: 'general_health', label: 'General Health' },
  { value: 'other', label: 'Other' },
];

const DIETARY_OPTIONS = [
  'Vegetarian', 'Vegan', 'Pescatarian', 'Gluten-Free', 'Dairy-Free',
  'Keto', 'Paleo', 'Low-Carb', 'Low-Sodium', 'Halal', 'Kosher', 'None'
];

const CUISINE_OPTIONS = [
  'American', 'Italian', 'Mexican', 'Asian', 'Mediterranean',
  'Indian', 'Japanese', 'Thai', 'Middle Eastern', 'French'
];

const SESSION_OPTIONS = [15, 30, 45, 60, 75, 90, 120];

const CoachInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  // Profile step state
  const [step, setStep] = useState('invite'); // 'invite' | 'profile'
  const [clientId, setClientId] = useState(null);
  const [acceptedCoachType, setAcceptedCoachType] = useState(null);
  const [acceptData, setAcceptData] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  // Fitness profile form fields
  const [goals, setGoals] = useState([]);
  const [goalsOther, setGoalsOther] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [hasGymAccess, setHasGymAccess] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [sessionMinutes, setSessionMinutes] = useState(60);
  // Nutrition profile form fields
  const [nutritionGoal, setNutritionGoal] = useState('');
  const [nutritionGoalOther, setNutritionGoalOther] = useState('');
  const [dietaryRestrictions, setDietaryRestrictions] = useState([]);
  const [allergies, setAllergies] = useState('');
  const [cuisinePreferences, setCuisinePreferences] = useState([]);
  const [mealsPerDay, setMealsPerDay] = useState(3);
  const [cookingSkill, setCookingSkill] = useState('');
  const [budget, setBudget] = useState('medium');
  const [snackPreferences, setSnackPreferences] = useState('');
  const [dislikedFoods, setDislikedFoods] = useState('');
  const [nutritionNotes, setNutritionNotes] = useState('');
  
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isLoggedIn = !!user;

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await fetch(`${API_URL}/coach/invite/${token}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('This invite link is invalid or has expired.');
          } else {
            setError('Failed to load invite details.');
          }
          return;
        }
        
        const data = await response.json();
        setInvite(data);
      } catch (err) {
        console.error('Error fetching invite:', err);
        setError('Failed to connect to server.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvite();
  }, [token]);

  const acceptInvite = async () => {
    setAccepting(true);
    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/coach/invite/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to accept invite');
      }

      const data = await response.json();
      toast.success('Successfully connected with your coach!');
      
      const coachType = data.coachType || invite?.coachType || 'nutrition';
      setClientId(data.client?._id);
      setAcceptedCoachType(coachType);
      setAcceptData(data);

      if (coachType === 'fitness' || coachType === 'both') {
        try {
          const eqRes = await fetch(`${API_URL}/fitness/equipment`);
          if (eqRes.ok) setEquipment(await eqRes.json());
        } catch (e) { /* equipment list is optional */ }
        setStep('fitness-profile');
      } else if (coachType === 'nutrition') {
        setStep('nutrition-profile');
      } else {
        navigateAfterAccept(coachType, data);
      }
    } catch (err) {
      console.error('Error accepting invite:', err);
      toast.error(err.message);
    } finally {
      setAccepting(false);
    }
  };

  const navigateAfterAccept = (coachType, data) => {
    if (coachType === 'fitness' || coachType === 'both') {
      navigate('/fitness/my-workouts');
    } else if (data?.hasNutritionPlan && data?.client?.activeNutritionGoalId) {
      navigate(`/nutrition/planner?goalId=${data.client.activeNutritionGoalId}`);
    } else if (invite?.shareToken) {
      navigate(`/nutrition/shared/${invite.shareToken}`);
    } else {
      navigate('/meal-plan');
    }
  };

  const handleSaveFitnessProfile = async () => {
    setSavingProfile(true);
    try {
      const authToken = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/clients/${clientId}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          goals,
          goalsOther: goals.includes('other') ? goalsOther : undefined,
          experienceLevel,
          hasGymAccess,
          availableEquipment: selectedEquipment,
          daysPerWeek,
          sessionMinutes,
        })
      });
      if (res.ok) {
        toast.success('Fitness profile saved!');
      }
    } catch (e) {
      console.error('Error saving fitness profile:', e);
    } finally {
      setSavingProfile(false);
      if (acceptedCoachType === 'both') {
        setStep('nutrition-profile');
      } else {
        navigateAfterAccept(acceptedCoachType, acceptData);
      }
    }
  };

  const handleSaveNutritionProfile = async () => {
    setSavingProfile(true);
    try {
      const authToken = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/coach/clients/${clientId}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          nutritionProfile: {
            goal: nutritionGoal,
            goalOther: nutritionGoal === 'other' ? nutritionGoalOther : undefined,
            dietaryRestrictions,
            allergies: allergies ? allergies.split(',').map(a => a.trim()).filter(Boolean) : [],
            cuisinePreferences,
            mealsPerDay,
            cookingSkill,
            budget,
            snackPreferences: snackPreferences ? snackPreferences.split(',').map(s => s.trim()).filter(Boolean) : [],
            dislikedFoods: dislikedFoods ? dislikedFoods.split(',').map(f => f.trim()).filter(Boolean) : [],
            notes: nutritionNotes,
          }
        })
      });
      if (res.ok) {
        toast.success('Nutrition preferences saved!');
      }
    } catch (e) {
      console.error('Error saving nutrition profile:', e);
    } finally {
      setSavingProfile(false);
      navigateAfterAccept(acceptedCoachType, acceptData);
    }
  };

  const handleSkipProfile = () => {
    if (step === 'fitness-profile' && acceptedCoachType === 'both') {
      setStep('nutrition-profile');
    } else {
      navigateAfterAccept(acceptedCoachType, acceptData);
    }
  };

  const handleAcceptInvite = async () => {
    if (!isLoggedIn) {
      try {
        await pendingActionService.createAction('accept_invite', { inviteToken: token });
        toast('Please sign in to save your plan', { icon: 'ℹ️' });
        navigate('/');
      } catch (err) {
        console.error('Error creating pending action:', err);
        toast.error('Something went wrong. Please try again.');
      }
      return;
    }

    await acceptInvite();
  };

  const toggleGoal = (value) => {
    setGoals(prev => prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value]);
  };

  const toggleEquipment = (name) => {
    setSelectedEquipment(prev => prev.includes(name) ? prev.filter(e => e !== name) : [...prev, name]);
  };

  // Group equipment by category
  const equipmentByCategory = equipment.reduce((acc, eq) => {
    const cat = eq.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(eq);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="coach-invite">
        <div className="invite-card loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="coach-invite">
        <div className="invite-card error">
          <i className="fas fa-exclamation-circle"></i>
          <h2>Oops!</h2>
          <p>{error}</p>
          <button className="btn-secondary" onClick={() => navigate('/')}>
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  // Fitness profile onboarding step
  if (step === 'fitness-profile') {
    return (
      <div className="coach-invite">
        <div className="invite-card profile-step">
          <div className="invite-header">
            <div className="coach-avatar">
              <i className="fas fa-user-cog"></i>
            </div>
            <h1>Set Up Your Fitness Profile</h1>
            <p className="profile-subtitle">Help {invite?.coachName || 'your coach'} personalize your training</p>
            {acceptedCoachType === 'both' && <div className="step-indicator">Step 1 of 2 — Fitness</div>}
          </div>

          <div className="profile-form">
            <div className="profile-section">
              <h3><i className="fas fa-bullseye"></i> What are your goals?</h3>
              <div className="checkbox-grid">
                {GOAL_OPTIONS.map(opt => (
                  <label key={opt.value} className={`checkbox-card ${goals.includes(opt.value) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={goals.includes(opt.value)} onChange={() => toggleGoal(opt.value)} />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              {goals.includes('other') && (
                <input
                  type="text"
                  className="profile-text-input"
                  placeholder="Describe your goal..."
                  value={goalsOther}
                  onChange={e => setGoalsOther(e.target.value)}
                  maxLength={200}
                />
              )}
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-signal"></i> Experience Level</h3>
              <div className="radio-group">
                {['beginner', 'intermediate', 'advanced'].map(level => (
                  <label key={level} className={`radio-card ${experienceLevel === level ? 'selected' : ''}`}>
                    <input type="radio" name="experience" value={level} checked={experienceLevel === level} onChange={() => setExperienceLevel(level)} />
                    <span>{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-calendar-alt"></i> Training Availability</h3>
              <div className="training-row">
                <div className="training-field">
                  <label>Days per week</label>
                  <div className="days-selector">
                    {[1, 2, 3, 4, 5, 6, 7].map(d => (
                      <button key={d} type="button" className={`day-btn ${daysPerWeek === d ? 'active' : ''}`} onClick={() => setDaysPerWeek(d)}>{d}</button>
                    ))}
                  </div>
                </div>
                <div className="training-field">
                  <label>Session length</label>
                  <select value={sessionMinutes} onChange={e => setSessionMinutes(Number(e.target.value))}>
                    {SESSION_OPTIONS.map(m => (
                      <option key={m} value={m}>{m} min</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-dumbbell"></i> Equipment Access</h3>
              <label className={`checkbox-card gym-toggle ${hasGymAccess ? 'selected' : ''}`}>
                <input type="checkbox" checked={hasGymAccess} onChange={e => setHasGymAccess(e.target.checked)} />
                <span><i className="fas fa-building"></i> I have gym access</span>
              </label>
              {equipment.length > 0 && (
                <div className="equipment-list">
                  <p className="equipment-hint">Select equipment you have access to:</p>
                  {Object.entries(equipmentByCategory).map(([cat, items]) => (
                    <div key={cat} className="equipment-category">
                      <h4>{cat.charAt(0).toUpperCase() + cat.slice(1)}</h4>
                      <div className="checkbox-grid compact">
                        {items.map(eq => (
                          <label key={eq._id} className={`checkbox-card ${selectedEquipment.includes(eq.name) ? 'selected' : ''}`}>
                            <input type="checkbox" checked={selectedEquipment.includes(eq.name)} onChange={() => toggleEquipment(eq.name)} />
                            <span>{eq.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="invite-actions profile-actions">
            <button className="btn-primary" onClick={handleSaveFitnessProfile} disabled={savingProfile}>
              {savingProfile ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-check"></i> {acceptedCoachType === 'both' ? 'Save & Next' : 'Save & Continue'}</>}
            </button>
            <button className="btn-skip" onClick={handleSkipProfile}>
              Skip for Now <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Nutrition profile onboarding step
  if (step === 'nutrition-profile') {
    return (
      <div className="coach-invite">
        <div className="invite-card profile-step">
          <div className="invite-header">
            <div className="coach-avatar">
              <i className="fas fa-utensils"></i>
            </div>
            <h1>Your Nutrition Preferences</h1>
            <p className="profile-subtitle">Help {invite?.coachName || 'your coach'} create the perfect meal plan for you</p>
            {acceptedCoachType === 'both' && <div className="step-indicator">Step 2 of 2 — Nutrition</div>}
          </div>

          <div className="profile-form">
            <div className="profile-section">
              <h3><i className="fas fa-bullseye"></i> What's your primary nutrition goal?</h3>
              <div className="radio-group">
                {NUTRITION_GOAL_OPTIONS.map(opt => (
                  <label key={opt.value} className={`radio-card ${nutritionGoal === opt.value ? 'selected' : ''}`}>
                    <input type="radio" name="nutritionGoal" value={opt.value} checked={nutritionGoal === opt.value} onChange={() => setNutritionGoal(opt.value)} />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
              {nutritionGoal === 'other' && (
                <input type="text" className="profile-text-input" placeholder="Describe your goal..." value={nutritionGoalOther} onChange={e => setNutritionGoalOther(e.target.value)} maxLength={200} />
              )}
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-leaf"></i> Dietary Restrictions</h3>
              <div className="checkbox-grid">
                {DIETARY_OPTIONS.map(opt => (
                  <label key={opt} className={`checkbox-card ${dietaryRestrictions.includes(opt) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={dietaryRestrictions.includes(opt)} onChange={() => setDietaryRestrictions(prev => prev.includes(opt) ? prev.filter(d => d !== opt) : [...prev, opt])} />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-exclamation-triangle"></i> Food Allergies</h3>
              <input type="text" className="profile-text-input" placeholder="e.g. peanuts, shellfish, eggs (comma separated)" value={allergies} onChange={e => setAllergies(e.target.value)} />
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-globe-americas"></i> Cuisine Preferences</h3>
              <div className="checkbox-grid">
                {CUISINE_OPTIONS.map(opt => (
                  <label key={opt} className={`checkbox-card ${cuisinePreferences.includes(opt) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={cuisinePreferences.includes(opt)} onChange={() => setCuisinePreferences(prev => prev.includes(opt) ? prev.filter(c => c !== opt) : [...prev, opt])} />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-utensils"></i> Meals & Cooking</h3>
              <div className="training-row">
                <div className="training-field">
                  <label>Meals per day</label>
                  <div className="days-selector">
                    {[1, 2, 3, 4, 5, 6].map(n => (
                      <button key={n} type="button" className={`day-btn ${mealsPerDay === n ? 'active' : ''}`} onClick={() => setMealsPerDay(n)}>{n}</button>
                    ))}
                  </div>
                </div>
                <div className="training-field">
                  <label>Cooking skill</label>
                  <div className="radio-group compact">
                    {['beginner', 'intermediate', 'advanced'].map(level => (
                      <label key={level} className={`radio-card ${cookingSkill === level ? 'selected' : ''}`}>
                        <input type="radio" name="cookingSkill" value={level} checked={cookingSkill === level} onChange={() => setCookingSkill(level)} />
                        <span>{level.charAt(0).toUpperCase() + level.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="training-field">
                  <label>Budget</label>
                  <div className="radio-group compact">
                    {[{v:'low',l:'Budget-Friendly'},{v:'medium',l:'Moderate'},{v:'high',l:'Premium'}].map(opt => (
                      <label key={opt.v} className={`radio-card ${budget === opt.v ? 'selected' : ''}`}>
                        <input type="radio" name="budget" value={opt.v} checked={budget === opt.v} onChange={() => setBudget(opt.v)} />
                        <span>{opt.l}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-cookie-bite"></i> Snack & Food Preferences</h3>
              <div className="training-row" style={{flexDirection: 'column', gap: '0.75rem'}}>
                <div className="training-field" style={{flex: 1}}>
                  <label>Favorite snacks</label>
                  <input type="text" className="profile-text-input" placeholder="e.g. fruit, nuts, yogurt (comma separated)" value={snackPreferences} onChange={e => setSnackPreferences(e.target.value)} />
                </div>
                <div className="training-field" style={{flex: 1}}>
                  <label>Foods you dislike</label>
                  <input type="text" className="profile-text-input" placeholder="e.g. liver, olives, tofu (comma separated)" value={dislikedFoods} onChange={e => setDislikedFoods(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="profile-section">
              <h3><i className="fas fa-sticky-note"></i> Anything else?</h3>
              <textarea className="profile-text-input" rows="3" placeholder="Any other preferences, dietary needs, or notes for your coach..." value={nutritionNotes} onChange={e => setNutritionNotes(e.target.value)} maxLength={500} />
            </div>
          </div>

          <div className="invite-actions profile-actions">
            <button className="btn-primary" onClick={handleSaveNutritionProfile} disabled={savingProfile}>
              {savingProfile ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-check"></i> Save & Continue</>}
            </button>
            <button className="btn-skip" onClick={() => navigateAfterAccept(acceptedCoachType, acceptData)}>
              Skip for Now <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invite acceptance step (default)
  return (
    <div className="coach-invite">
      <div className="invite-card">
        <div className="invite-header">
          <div className="coach-avatar">
            {invite?.coachPicture ? (
              <img src={invite.coachPicture} alt={invite.coachName} />
            ) : (
              <i className="fas fa-user-tie"></i>
            )}
          </div>
          <h1>You've Been Invited!</h1>
        </div>

        <div className="invite-body">
          <p className="greeting">Hi {invite?.clientName || 'there'},</p>
          
          <p className="invite-message">
            <strong>{invite?.coachName || 'Your Coach'}</strong>
            {invite?.businessName && (
              <span> from <strong>{invite.businessName}</strong></span>
            )}
            {' '}has invited you to join their {invite?.coachType === 'fitness' ? 'fitness coaching' : invite?.coachType === 'both' ? 'coaching' : 'nutrition coaching'} program on FreshFitLab.
          </p>

          {invite?.hasNutritionPlan && (
            <div className="plan-badge">
              <i className="fas fa-clipboard-list"></i>
              <span>A nutrition plan has been created for you!</span>
            </div>
          )}

          {invite?.fitnessInfo && (
            <div className="plan-badge">
              <i className="fas fa-dumbbell"></i>
              <span>
                {invite.fitnessInfo.workoutCount} workout{invite.fitnessInfo.workoutCount !== 1 ? 's' : ''} and {invite.fitnessInfo.programCount} program{invite.fitnessInfo.programCount !== 1 ? 's' : ''} available
              </span>
            </div>
          )}

          <div className="benefits">
            <h3>What you'll get:</h3>
            <ul>
              {(!invite?.coachType || invite?.coachType === 'nutrition' || invite?.coachType === 'both') && (
                <>
                  <li><i className="fas fa-check-circle"></i>Personalized nutrition guidance</li>
                  <li><i className="fas fa-check-circle"></i>Custom meal planning tools</li>
                </>
              )}
              {(invite?.coachType === 'fitness' || invite?.coachType === 'both') && (
                <>
                  <li><i className="fas fa-check-circle"></i>Personalized workout programs</li>
                  <li><i className="fas fa-check-circle"></i>Coach-assigned training schedules</li>
                </>
              )}
              {(!invite?.coachType || invite?.coachType === 'nutrition' || invite?.coachType === 'both') && (
                <li><i className="fas fa-check-circle"></i>Easy grocery shopping with Instacart integration</li>
              )}
              <li>
                <i className="fas fa-check-circle"></i>
                {invite?.coachType === 'fitness' ? 'Access to an exercise library' : 'Access to a library of healthy recipes'}
              </li>
            </ul>
          </div>
        </div>

        <div className="invite-actions">
          {invite?.hasNutritionPlan && invite?.shareToken && (
            <button className="btn-view-plan" onClick={() => navigate(`/nutrition/shared/${invite.shareToken}`)}>
              <i className="fas fa-eye"></i> View My Plan
            </button>
          )}

          {isLoggedIn ? (
            <>
              <button className="btn-primary" onClick={handleAcceptInvite} disabled={accepting}>
                {accepting ? (
                  <><i className="fas fa-spinner fa-spin"></i> Accepting...</>
                ) : (
                  <><i className="fas fa-check"></i> Accept & Save to My Account</>
                )}
              </button>
              <p className="logged-in-note">Signed in as <strong>{user?.email || user?.name}</strong></p>
            </>
          ) : (
            <>
              <button className="btn-primary" onClick={handleAcceptInvite}>
                <i className="fas fa-sign-in-alt"></i> Sign In to Save Plan
              </button>
              <p className="signup-note">
                <i className="fas fa-info-circle"></i> Sign in to save this plan to your account and track your progress
              </p>
            </>
          )}
        </div>

        <div className="invite-footer">
          <p className="privacy-note">
            <i className="fas fa-lock"></i>
            Your information is secure and won't be shared.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CoachInvite;
